import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Controls from "./Controls";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { Fortune, Point, Edge } from "./Fortune";

import {
  Text,
  Circle,
  Group,
  Layer,
  Line,
  Stage,
  Star,
  Arrow,
} from "react-konva";
import Konva from "konva";

function area(x1, y1, x2, y2, x3, y3) {
  return Math.abs((x1 * (y2 - y3) + x2 * (y3 - y1) + x3 * (y1 - y2)) / 2.0);
}

function isInside(x1, y1, x2, y2, x3, y3, x, y) {
  let A = area(x1, y1, x2, y2, x3, y3);

  let A1 = area(x, y, x2, y2, x3, y3);

  let A2 = area(x1, y1, x, y, x3, y3);

  let A3 = area(x1, y1, x2, y2, x, y);

  return Math.abs(A - A1 - A2 - A3) < 10;
}

const initPolys = [
  [150, 150, 180, 250, 280, 250, 300, 180],
  [550, 150, 580, 250, 680, 250, 800, 170, 650, 100],
  [300, 500, 400, 600, 700, 600, 800, 500],
];

function checkPointsInInitPolys(v: Point) {
  for (let i = 0; i < 3; i++) {
    if (
      isInside(
        initPolys[i][0],
        initPolys[i][1],
        initPolys[i][2],
        initPolys[i][3],
        initPolys[i][4],
        initPolys[i][5],
        v.x,
        v.y
      ) ||
      isInside(
        initPolys[i][0],
        initPolys[i][1],
        initPolys[i][4],
        initPolys[i][5],
        initPolys[i][6],
        initPolys[i][7],
        v.x,
        v.y
      )
    )
      return true;
  }
  if (
    isInside(
      initPolys[1][0],
      initPolys[1][1],
      initPolys[1][6],
      initPolys[1][7],
      initPolys[1][8],
      initPolys[1][9],
      v.x,
      v.y
    )
  )
    return true;
  else return false;
}

const initDiscretePoints = [
  // first poly
  new Point(150, 150),
  new Point(165, 200),
  new Point(180, 250),
  new Point(230, 250),
  new Point(280, 250),
  new Point(290, 215),
  new Point(300, 180),
  new Point(250, 170),
  new Point(200, 160),
  // second poly
  new Point(550, 150),
  new Point(565, 200),
  new Point(580, 250),
  new Point(630, 250),
  new Point(680, 250),
  new Point(740, 210),
  new Point(800, 170),
  new Point(750, 140),
  new Point(700, 120),
  new Point(650, 100),
  new Point(600, 125),
  // // third poly
  new Point(300, 500),
  new Point(355, 550),
  new Point(400, 600),
  new Point(500, 600),
  new Point(600, 600),
  new Point(700, 600),
  new Point(750, 550),
  new Point(800, 500),
  new Point(700, 501),
  new Point(600, 499),
  new Point(500, 501),
  new Point(400, 500),
];

function Main() {
  const [points, setPoints] = useState([]);
  const [polys, setPolys] = useState(initPolys);
  const [fortune, setFortune] = useState(new Fortune(points, 300, 300));
  const [edges, setEdges] = useState<Edge[]>([]);
  const [vertices, setVertices] = useState<Point[]>([]);
  const [hidePoints, setHidePoints] = useState(false);
  const divRef = useRef(null);

  const [dimensions, setDimensions] = useState({
    width: 0,
    height: 0,
  });
  const groupRef: React.RefObject<Konva.Group> = useRef();

  useEffect(() => {
    if (dimensions.height !== 0 && dimensions.width !== 0)
      removeVerticesOnBoundaries();
  }, [dimensions]);

  const restart = () => {
    setShowNewStartPoint(false);
    setShowNewTargetPoint(false);
  };

  const discretize = () => {
    setPoints(initDiscretePoints);
    setPolys([]);
    setEdges([]);
    setVertices([]);
    setHidePoints(false);
  };

  const discretizeBoundary = () => {
    let newPoints = [...initDiscretePoints];
    let grid = 50;
    for (let i = 0; i < grid; i++) {
      let randomnum = Math.random();
      newPoints.push(new Point(1, (i * dimensions.height) / grid + randomnum));
      newPoints.push(
        new Point(
          dimensions.width,
          (i * dimensions.height) / grid + randomnum + 1
        )
      );
      if (i !== 0) {
        newPoints.push(
          new Point((i * dimensions.width) / grid + randomnum + 1, 1)
        );
        newPoints.push(
          new Point(
            (i * dimensions.width) / grid + randomnum + 2,
            dimensions.height
          )
        );
      }
    }
    setPoints(newPoints);
    setPolys([]);
    setEdges([]);
    setVertices([]);
    setHidePoints(false);
    return newPoints;
  };

  const draw = () => {
    let newPoints = discretizeBoundary();
    let newFortune = new Fortune(
      newPoints,
      dimensions.width,
      dimensions.height
    );
    newFortune.update();
    setEdges(newFortune.edges);
    setVertices(newFortune.voronoi_vertex);
    return { edges: newFortune.edges, vertices: newFortune.voronoi_vertex };
  };

  const resumePolygons = () => {
    let { edges, vertices } = draw();
    setPolys(initPolys);
    setHidePoints(true);
    return { edges, vertices };
  };

  const removeVerticesInPolygons = () => {
    let { edges, vertices } = resumePolygons();
    setVertices(
      vertices.filter((v) => v).filter((v) => !checkPointsInInitPolys(v))
    );
    setEdges(
      edges
        .filter((e) => e)
        .filter(
          (e) =>
            !checkPointsInInitPolys(e.end) && !checkPointsInInitPolys(e.start)
        )
        // special handle
        .filter((e) => !(e.start.x == 550 && e.start.y == 87.5))
        .filter((e) => !(e.start.x == 800 && Math.round(e.start.y) == 113))
        .filter((e) => !(e.start.x == 750 && e.start.y == 78.75))
        .filter((e) => !(e.start.x == 600 && e.start.y == 50))
    );
  };
  const removeVerticesOnBoundaries = () => {
    let { edges, vertices } = resumePolygons();
    setVertices(
      vertices
        .filter((v) => v)
        .filter((v) => !checkPointsInInitPolys(v))
        .filter((v) => v.x > 5 && v.y > 5)
        .filter(
          (v) => v.x < dimensions.width - 5 && v.y < dimensions.height - 5
        )
    );
    setEdges(
      edges
        .filter((e) => e)
        .filter(
          (e) =>
            !checkPointsInInitPolys(e.end) && !checkPointsInInitPolys(e.start)
        )
        .filter((e) => e.end.x > 5 && e.end.y > 5)
        .filter(
          (e) =>
            e.end.x < dimensions.width - 5 && e.end.y < dimensions.height - 5
        )
        .filter((e) => e.start.x > 5 && e.start.y > 5)
        .filter(
          (e) =>
            e.start.x < dimensions.width - 5 &&
            e.start.y < dimensions.height - 5
        )
        // special handle
        .filter((e) => !(e.start.x == 550 && e.start.y == 87.5))
        .filter((e) => !(e.start.x == 800 && Math.round(e.start.y) == 113))
        .filter((e) => !(e.start.x == 750 && e.start.y == 78.75))
        .filter((e) => !(e.start.x == 600 && e.start.y == 50))
        .filter(
          (e) =>
            !(Math.abs(e.start.x - 96) < 2 && Math.abs(e.start.y - 616) < 2)
        )
        .filter(
          (e) =>
            !(Math.abs(e.start.x - 845) < 2 && Math.abs(e.start.y - 621) < 2)
        )
    );
  };

  useEffect(() => {
    if (divRef.current?.offsetHeight && divRef.current?.offsetWidth) {
      setDimensions({
        width: divRef.current.offsetWidth,
        height: divRef.current.offsetHeight,
      });
    }
    if (divRef.current?.offsetHeight == 0 && divRef.current?.offsetWidth) {
      setDimensions({
        width: divRef.current.offsetWidth,
        height: divRef.current.offsetWidth,
      });
    }
  }, [divRef]);
  const [showNewStartPoint, setShowNewStartPoint] = useState(false);
  const [showNewTargetPoint, setShowNewTargetPoint] = useState(false);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "column", md: "row" },
        flexGrow: 1,
      }}
    >
      <Box sx={{ flex: 7 }}>
        <Controls
          restart={restart}
          showNewStartPoint={() => setShowNewStartPoint(true)}
          showNewTargetPoint={() => setShowNewTargetPoint(true)}
        />
      </Box>
      <Box
        sx={{
          flex: 13,
          minHeight: 600,
          backgroundColor: "#efefef",
          position: "relative",
          height: "100%",
        }}
        ref={divRef}
      >
        <Box
          sx={{
            position: "absolute",
            display: "flex",
            flexDirection: "row",
            width: "100%",
            justifyContent: "center",
            margin: 2,
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              marginRight: 5,
            }}
          ></Box>
        </Box>
        <Stage width={dimensions.width} height={dimensions.height}>
          <Layer>
            <Group ref={groupRef}>
              <Star
                x={50}
                y={485}
                numPoints={6}
                innerRadius={4}
                outerRadius={7}
                strokeWidth={5}
                stroke={"#33ccff"}
                fill={"#33ccff"}
              />
              <Text x={20} y={450} fontSize={16} text={"start point"} />
              <Star
                x={780}
                y={130}
                numPoints={6}
                innerRadius={4}
                outerRadius={7}
                strokeWidth={5}
                stroke={"red"}
                fill={"red"}
              />
              <Text x={800} y={125} fontSize={16} text={"target point"} />
              {!hidePoints &&
                points.map((p, i) => (
                  <Circle key={i} radius={5} fill="orange" x={p.x} y={p.y} />
                ))}
              {polys.map((p, i) => (
                <Line
                  points={p}
                  key={i}
                  strokeWidth={5}
                  fill={"orange"}
                  closed
                  stroke={"orange"}
                />
              ))}
              {edges
                .filter((e) => e)
                .map((e, i) => {
                  return (
                    <Line
                      key={i}
                      strokeWidth={10}
                      stroke={"grey"}
                      points={[e.start.x, e.start.y, e.end.x, e.end.y]}
                    />
                  );
                })}
              {vertices
                .filter((e) => e)
                .map((p, i) => {
                  return (
                    <Circle key={i} radius={5} fill="black" x={p.x} y={p.y} />
                  );
                })}
              {showNewStartPoint && (
                <>
                  <Arrow
                    x={0}
                    y={0}
                    pointerLength={10}
                    pointerWidth={10}
                    points={[0, 485, 150, 485]}
                    stroke={"#33ccff"}
                    strokeWidth={4}
                    fill={"#33ccff"}
                  />
                  <Star
                    x={150}
                    y={485}
                    numPoints={6}
                    innerRadius={4}
                    outerRadius={7}
                    strokeWidth={5}
                    stroke={"#3f51b5"}
                    fill={"#3f51b5"}
                  />
                  <Text
                    x={130}
                    y={505}
                    fontSize={16}
                    text={"new start point"}
                  />
                </>
              )}
              {showNewTargetPoint && (
                <>
                  <Arrow
                    x={0}
                    y={0}
                    pointerLength={10}
                    pointerWidth={10}
                    points={[765, 155, 800, 90]}
                    stroke={"red"}
                    strokeWidth={4}
                    fill={"red"}
                  />
                  <Star
                    x={805}
                    y={82}
                    numPoints={6}
                    innerRadius={4}
                    outerRadius={7}
                    strokeWidth={5}
                    stroke={"#f48fb1"}
                    fill={"#f48fb1"}
                  />
                  <Text
                    x={790}
                    y={50}
                    fontSize={16}
                    text={"new target point"}
                  />
                </>
              )}
            </Group>
          </Layer>
        </Stage>
      </Box>
    </Box>
  );
}

interface TabPanelProps {
  children?: React.ReactNode;
}

function PathPlanningVoronoi(props: TabPanelProps) {
  const { children, ...other } = props;

  return (
    <HelmetProvider>
      <Main />
      <Helmet>
        <title>Visualization of Path Planning with Voronoi Diagram</title>
        <meta
          name="description"
          content="Explore interactive path planning with Voronoi Diagrams: Learn how to connect start and target points to Voronoi edges and apply path planning algorithms like A* and Dijkstra. Perfect for understanding and visualizing efficient path planning techniques in computational geometry."
        />
      </Helmet>
    </HelmetProvider>
  );
}

export default PathPlanningVoronoi;
